import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { AppSettingService } from '../app.service';
import { UserInfoService, IUserProfile } from '../user/user-info.service';

export const WidgetNames: string[] = [
	"CareerExploration",
	"DNAResponse",
	"InterestSurvey",
	"JobPosting",
	"LandingPage",
	"Login",
	"Map",
	"MyActivity",
	"MyConnection",
	"MyFollowing",
	"MyJourney",
	"MyResource",
	"ProfileDashboard",
	"RecommendedCareer",
	"Signup",
	"SkillProfiler",
	"SkillsGap",
	"StudentPicker",
	"Settings",
	"SystemDashboard",
	"ConnectionClass"
];
export type TWidgetName = typeof WidgetNames[number];

export const WidgetRoles: string[] = ["systemadmin", "zoneadmin", "schooladmin", "teacher", "student", "parent", "zonestaff", "schoolstaff", "other", "anonymous"];
export type TWidgetRole = typeof WidgetRoles[number];

export interface IWidgetLayout {
	ComponentClassName: string,
	UiName: string,
	GridsterItemJson: string,
	SequenceNumber: number,
	IsOn: boolean,
	IsLocked: boolean,
	IsVisible: boolean
}

export interface ISetting {
	Name: string,
	Value: string
}
export interface IWidgetSetting {
	Name: TWidgetName,
	FormattedName: string,
	Enable: boolean,
	Collapse: boolean,
	Setting: ISetting[],
	Layout: IWidgetLayout[],
	HasWidget: boolean
}

export interface IWidgetRole {
	PersonTypeId: string,
	PersonType: TWidgetRole
	Enable: boolean
}

export interface IWidgetHomePage {
	PersonTypeId: string,
	PersonType: TWidgetRole
	WidgetId: string
	Name: TWidgetName,
	FormattedName: string;
	newHome: IWidget
}

export interface IWidget {
	WidgetId: string,
	Name: TWidgetName,
	FormattedName: string,
	Enable: boolean,
	HasHomePage: boolean,
	ManageByRole: boolean,
	Roles: IWidgetRole[]
}

export interface IRoleManagement {
	Widgets: IWidget[],
	HomePages: IWidgetHomePage[]
}

@Injectable({
	providedIn: 'root'
})
export class WidgetAPIService {
	constructor(
		private http: HttpClient,
		private userInfo: UserInfoService
	) { }

	private widgetUrl = AppSettingService.appConfig.JourneyAPIUrl + "Widget";

	//#region user
	saveUserWidgetCollapse(name: TWidgetName, collapse: boolean): Rx.Observable<any> {
		let data: any = {
			Collapse: collapse,
		};
		return this.http.patch<any>(
			this.widgetUrl + "/User/" + name,
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveUserWidgetSetting(name: TWidgetName, setting: ISetting[]): Rx.Observable<any> {
		let data: any = {
			Setting: setting
		};
		return this.http.patch<any>(
			this.widgetUrl + "/User/" + name,
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveUserWidgetLayout(name: TWidgetName, layout: IWidgetLayout[]): Rx.Observable<any> {
		let data: any = {
			Layout: layout
		};
		return this.http.patch<any>(
			this.widgetUrl + "/User/" + name,
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getUserWidget(name: TWidgetName): Rx.Observable<IWidgetSetting> {
		return this.http.get<IWidgetSetting>(
			this.widgetUrl + "/User/" + name,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	//use this to see available widgets (from outside 2884)
	getUserWidgets(): Rx.Observable<IWidgetSetting> {
		return this.http.get<IWidgetSetting>(
			this.widgetUrl + "/User",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	get allowedWidgets(): TWidgetName[] {
		try {
			let p: IUserProfile = this.userInfo.getProfile();
			return p.AllowedWidgets;
		}
		catch (ex) {
			return [];
		}
	}

	isWidgetAllowed(widget: TWidgetName): boolean {
		return this.allowedWidgets.find(x => x === widget) != undefined;
	}
	//#endregion user


	//#region client
	saveClientWidgetSetting(name: TWidgetName, setting: ISetting[]): Rx.Observable<any> {
		let data: any = {
			Setting: setting
		};
		return this.http.patch<any>(
			this.widgetUrl + "/Client/" + name,
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveClientWidgetLayout(name: TWidgetName, layout: IWidgetLayout[]): Rx.Observable<any> {
		let data: any = {
			Layout: layout
		};
		return this.http.patch<any>(
			this.widgetUrl + "/Client/" + name,
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getClientWidget(name: TWidgetName): Rx.Observable<IWidgetSetting> {
		return this.http.get<IWidgetSetting>(
			this.widgetUrl + "/User/" + name,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#endregion client


	//#region system
	getRoleManagement(zoneId: string): Rx.Observable<IRoleManagement> {
		return this.http.get<IRoleManagement>(
			this.widgetUrl + "/System/RoleManagement/" + zoneId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveRoleManagement(zoneId: string, roleManagement: IRoleManagement): Rx.Observable<any> {
		return this.http.post<any>(
			this.widgetUrl + "/System/RoleManagement/" + zoneId,
			roleManagement,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveSystemClientWidgetSetting(zoneId: string, name: TWidgetName, setting: ISetting[]): Rx.Observable<any> {
		let data: any = {
			Setting: setting
		};
		return this.http.patch<any>(
			this.widgetUrl + "/System/" + name + "/" + zoneId,
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	saveSystemClientWidgetLayout(zoneId: string, name: TWidgetName, layout: IWidgetLayout[]): Rx.Observable<any> {
		let data: any = {
			Layout: layout
		};
		return this.http.patch<any>(
			this.widgetUrl + "/System/" + name + "/" + zoneId,
			data,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getSystemClientWidget(zoneId: string, name: TWidgetName): Rx.Observable<IWidgetSetting> {
		return this.http.get<IWidgetSetting>(
			this.widgetUrl + "/System/" + name + "/" + zoneId,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}
	//#region system
}


